/**
 * @copyright 2016 CBC/Radio-Canada.
 * @license Unlicensed. All rights reserved.
 */

import PropTypes from "prop-types";
import { PageHeading } from "../../components/Heading/component";
import classNames from "classnames";
import { useSelector, useDispatch } from "react-redux";

import Main from "./Main/component";
import Region, { RegionVideo } from "./Region/component";
import Politics from "./Politics/component";
import Other from "./Other/component";
import RegionIntro from "./RegionIntro/component";
import Ad from "../../components/Ad/component";
import PageAnalytics from "../../components/PageAnalytics";
import MaybeError from "../../components/MaybeError/component";

import { split_content_lists } from "../../utils/dataTransform/utils";
import { getLandingAnalytics } from "../../utils/analytics/library";
import {
    FEATURED_DENSE_COUNT,
    POLITICS_FEATURED_DENSE_COUNT,
} from "../../components/ContentArea/constants";
import { TYPE_LANDING } from "../../utils/analytics/constants";
import { REGION_REGEX, OTHER_REGEX, REGION_VIDEO_REGEX } from "./constants";
import { SECTIONS_WITH_SWIMLANE } from "../../constants/sections";
import { fetchLandingNewsPageData } from "../LandingNews/initData";
import { PAGE_COMPONENT_UNIVERSAL_CLASS_NAME } from "../../constants/page";
import { fetch_content_load_more } from "../../actions/Content/dispatchers";
import usePageFetch from "../../hooks/usePageFetch";
import { sendGoal } from "../../components/UserSplitTest/library/goals";

/**
 * News landing page.
 */

export const LandingNews = (props) => {
    const { hasNextPage, list, contentPackage } = useSelector(
        (state) => state.content
    );
    const rightRail = useSelector((state) => state.rightRail);
    const curatedPlaylistItems = useSelector(
        (state) => state.video.curatedPlaylist.items
    );
    const analytics = useSelector((state) => state.tracking);
    const { path, title, baseSection, subSection, statusCode, meta } =
        useSelector((state) => state.app);
    const dispatch = useDispatch();
    const cutoffIndex =
        path === "news/politics"
            ? POLITICS_FEATURED_DENSE_COUNT
            : FEATURED_DENSE_COUNT;
    const fetchLandingData = () =>
        dispatch(fetchLandingNewsPageData(props.match.url, props.match.params));

    usePageFetch(props.match, fetchLandingData);

    const getNewsPageMarkup = () => {
        const contentProps = {
            subSection: subSection,
            viewport: props.viewport,
            hasTopStories: true,
            contentPackage: contentPackage,
            renderCuratedPlaylist:
                curatedPlaylistItems.length > 0 ? true : false,
            loadMore: hasNextPage ?? false,
            loadMoreFetch: fetch_content_load_more(path),
        };

        const splitProps = getSplitContentArea(list, cutoffIndex, contentProps);
        const route = props.match.path;

        // If local or other regex, render respective components, otherwise render <Main />
        return path === "news/politics" ? (
            renderPoliticsTemplate(splitProps)
        ) : path.match(REGION_VIDEO_REGEX) ? (
            <RegionVideo splitProps={splitProps} path={path} />
        ) : route.match(REGION_REGEX) ? (
            renderRegionTemplate(splitProps)
        ) : route.match(OTHER_REGEX) ? (
            renderOtherTemplate(splitProps, contentProps)
        ) : (
            renderMainTemplate(splitProps)
        );
    };

    const renderMainTemplate = (splitProps) => {
        return <Main viewport={props.viewport} splitProps={splitProps} />;
    };

    const renderPoliticsTemplate = (splitProps) => {
        return (
            <Politics
                path={props.match.url.replace(/^\//, "")}
                splitProps={splitProps}
            />
        );
    };

    const renderRegionTemplate = (splitProps) => {
        return (
            <Region
                path={props.match.url.replace(/^\//, "")}
                splitProps={splitProps}
            />
        );
    };

    const renderOtherTemplate = (splitProps, contentProps) => {
        return (
            <Other
                swimlane={SECTIONS_WITH_SWIMLANE[path]}
                contentList={list}
                subSection={subSection}
                contentProps={contentProps}
                splitProps={splitProps}
            />
        );
    };

    const analyticsProps = getLandingAnalytics({ meta, analytics });

    const cn = classNames("landingNews", PAGE_COMPONENT_UNIVERSAL_CLASS_NAME);

    const sendGoalProps = props.abTestGoal
        ? {
              onClick: (event) => {
                  if (event.target?.closest(".contentArea")) {
                      sendGoal(props.abTestGoal);
                  }
              },
          }
        : {};

    return (
        <MaybeError statusCode={statusCode}>
            <div className={cn} {...sendGoalProps} data-testid="landing-news">
                <PageHeading hidden={true} text={title} />
                <RegionIntro
                    path={path}
                    baseSection={baseSection}
                    rightrail={rightRail}
                />
                {getNewsPageMarkup()}
                <Ad type="intercept" targeting={{ plc: "feed" }} lazy={true} />
                <PageAnalytics dtmData={analyticsProps} type={TYPE_LANDING} />
            </div>
        </MaybeError>
    );
};
/**
 *
 * @param {Array} list
 * @param {number} cutoffIndex
 * @param {Object} contentProps
 * @returns {Object} Object with keys : featuredProps and moreContentProps
 */
export const getSplitContentArea = (list, cutoffIndex, contentProps) => {
    const numTopStories = 0;
    const hasHighlightedStories = false;
    // Split the items into the FeaturedArea and Content lists
    const [featuredList, contentList] = split_content_lists(
        list,
        numTopStories,
        hasHighlightedStories,
        cutoffIndex
    );
    const featuredProps = Object.assign({}, contentProps, {
        content: featuredList,
        cutoffIndex: cutoffIndex,
        isDenseFeatured: true,
        hasTopStories: false,
    });

    const moreContentProps = Object.assign({}, contentProps, {
        content: contentList,
        hasFeature: false,
        hasSidebar: false,
        contentPackage: null,
    });

    return {
        featuredProps,
        moreContentProps,
    };
};

LandingNews.propTypes = {
    viewport: PropTypes.string,
};

export default LandingNews;
